import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import { app } from '../../app';
import {
  BasicBlockProps,
  blockColorMap,
  BlockId,
  ColorId,
  colorToBlockId,
} from '../../defs/block';
import { CollideEvent, CollideId } from '../../defs/collide';
import {
  BasicBlockEntity,
  despawnBlockEntityAsync,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { blockNearestNeighbors } from '../../util/blockTools';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { mapGetPan } from '../../util/mapTools';
import { matchFindGroup } from '../../util/matchTools';
import { BreakEffect } from '../effects/BreakEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { MergeEffect } from '../effects/MergeEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    basic block collision resolver
*/
export class BasicCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: BasicBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: BasicBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap() {
    // find match group
    const group = matchFindGroup(this._scene, this._entity);

    //TODO: put following into an effect
    // if found enough
    if (group) {
      const collisionTracker = getCollisionTracker();

      // block collide neighbor positions
      for (const neighbor of group.neighbors) {
        collisionTracker.collideAt(
          this._scene.sessionEntity.c.map,
          neighbor.c.position.mapPosition,
          'block',
          {
            type: 'block',
            entity: this._entity,
          },
        );
      }

      // notify group break event
      this._scene.events.publish({
        id: 'group',
        cause: 'basic',
        count: group.matches.length,
      });

      // spawn power block?
      const spawnBlockId = this._getSpawnBlockFromSymboledEntity();

      if (spawnBlockId) {
        // play sound
        const [baseBlock] = this._entity.c.blockBasic.symbolId.split('_');
        app.sound.play('puzzle-power-block-form', {
          pan: mapGetPan(this._entity.c.position.mapPosition),
        });

        this._scene.recordPowerBlockCreated(baseBlock as Match2PowerupType);

        await new MergeEffect(
          this._scene,
          group.matches.map((match) => match.base),
          this._entity.c.position.mapPosition,
          spawnBlockId,
        ).execute();
      } else {
        for (const match of group.matches) {
          match.base.c.block.collide('attack');
        }
      }

      return true;
      // else invalid effect
    }
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveAttack(event: CollideEvent) {
    // attack basic block
    await this._attack(event);
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attack(event: CollideEvent) {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;

    // play break sound
    app.sound.play('puzzle-basic-destroy', {
      pan: mapGetPan(position),
    });

    // collide neighbors for blocks that got destroyed with a lollipop (cube)
    if (event.collider?.type === 'cube') {
      const collisionTracker = getCollisionTracker();
      const neighbors = blockNearestNeighbors(
        this._scene.sessionEntity.c.map,
        entity,
        () => true,
      )!.filter((n) => n !== undefined);

      neighbors.forEach((neighbor) => {
        collisionTracker.collideAt(
          this._scene.sessionEntity.c.map,
          neighbor.c.position.mapPosition,
          'block',
          {
            type: 'block',
            entity: this._entity,
          },
        );
      });
    }

    // execute break effect
    await new BreakEffect(this._scene, {
      position,
      size: entity.c.block.props,
      color: blockColorMap[this._getColor()].base,
    }).execute();

    // despawn block entity
    await despawnBlockEntityAsync(entity, 'attack');
  }

  // private: support
  //-------------------------------------------------------------------------
  private _getSpawnBlockFromSymboledEntity(): BlockId | undefined {
    switch (this._entity.c.blockBasic.symbolId) {
      case 'bomb_red':
      case 'bomb_green':
      case 'bomb_blue':
      case 'bomb_yellow':
      case 'bomb_purple':
        return 'bomb';

      case 'cube_red':
      case 'cube_green':
      case 'cube_blue':
      case 'cube_yellow':
      case 'cube_purple':
        return this._getSpawnCubeBlockFromSymboledEntity();

      case 'rocket_red':
      case 'rocket_green':
      case 'rocket_blue':
      case 'rocket_yellow':
      case 'rocket_purple':
        return this._getSpawnRocketBlockFromSymboledEntity();
      default:
        return undefined;
    }
  }

  private _getSpawnCubeBlockFromSymboledEntity(): BlockId | undefined {
    // match color to cube
    return colorToBlockId[this._getColor()].cube;
  }

  private _getSpawnRocketBlockFromSymboledEntity(): BlockId | undefined {
    return Math.random() < 0.5 ? 'rocketHorizontal' : 'rocketVertical';
  }

  private _getColor(): ColorId {
    return (this._entity.c.block.props as BasicBlockProps).color;
  }
}
